import { useMemo, useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Check, X } from "lucide-react";
import { DataTable } from "@/components/common/DataTable";
import { ListToolbar } from "@/components/common/ListToolbar";
import { DataPagination } from "@/components/common/DataPagination";
import { usePaginatedQuery } from "@/hooks/usePaginatedQuery";
import { useUrlState } from "@/lib/urlState";
import { queryKeys } from "@/lib/queryKeys";
import { exportRowsAsCsv } from "@/lib/csv";

interface CategorySuggestion {
  id: string;
  category_name: string;
  description: string | null;
  grade: string;
  status: string;
  created_at: string;
  subject_id: string;
  subjects?: { name: string } | null;
}

const STATUS_OPTIONS = ["all", "pending", "approved", "rejected"] as const;

const CategorySuggestions = () => {
  const { toast } = useToast();
  const qc = useQueryClient();
  const url = useUrlState();
  const search = url.get("q") ?? "";
  const statusFilter = (url.get("status") as (typeof STATUS_OPTIONS)[number]) ?? "pending";
  const [processingIds, setProcessingIds] = useState<Record<string, boolean>>({});

  const paginated = usePaginatedQuery<CategorySuggestion>({
    queryKey: queryKeys.categorySuggestions.list({
      page: 0,
      pageSize: 25,
      filters: { search, statusFilter },
    }),
    table: "category_suggestions",
    select: "id,category_name,description,grade,status,created_at,subject_id,subjects(name)",
    syncToUrl: true,
    orderBy: { column: "created_at", ascending: false },
    filters: (q) => {
      let next = q;
      if (statusFilter !== "all") {
        next = next.eq("status", statusFilter);
      }
      if (search) {
        next = next.or(`category_name.ilike.%${search}%,grade.ilike.%${search}%`);
      }
      return next;
    },
  });

  const updateSuggestionStatus = async (suggestion: CategorySuggestion, status: string) => {
    setProcessingIds((p) => ({ ...p, [suggestion.id]: true }));
    try {
      const { data: userData } = await supabase.auth.getUser();
      const { error } = await supabase
        .from("category_suggestions")
        .update({
          status: status as "pending" | "approved" | "rejected",
          reviewed_at: new Date().toISOString(),
          reviewed_by: userData.user?.id,
        })
        .eq("id", suggestion.id);
      if (error) throw error;

      if (status === "approved") {
        const { error: catErr } = await supabase.from("subjects_categories").insert({
          name: suggestion.category_name,
          description: suggestion.description,
          subject_id: suggestion.subject_id,
        });
        if (catErr) throw catErr;
      }

      toast({
        title: "Success",
        description:
          status === "approved" ? "Approved and category created" : "Suggestion rejected",
      });
      qc.invalidateQueries({ queryKey: ["categorySuggestions"] });
    } catch (err) {
      console.error(err);
      toast({
        title: "Error",
        description: `Failed to ${status} suggestion`,
        variant: "destructive",
      });
    } finally {
      setProcessingIds((p) => ({ ...p, [suggestion.id]: false }));
    }
  };

  const getStatusBadge = (status: string) => {
    const map: Record<string, string> = {
      pending: "bg-yellow-50 text-yellow-700 border-yellow-200",
      approved: "bg-green-50 text-green-700 border-green-200",
      rejected: "bg-red-50 text-red-700 border-red-200",
    };
    return (
      <Badge variant="outline" className={map[status] ?? ""}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const columns = useMemo<ColumnDef<CategorySuggestion, unknown>[]>(
    () => [
      { accessorKey: "category_name", header: "Category" },
      {
        accessorKey: "description",
        header: "Description",
        cell: ({ row }) =>
          row.original.description || (
            <span className="text-muted-foreground italic">No description</span>
          ),
      },
      { accessorKey: "grade", header: "Grade" },
      {
        id: "subject",
        header: "Subject",
        cell: ({ row }) => row.original.subjects?.name ?? "—",
      },
      {
        accessorKey: "created_at",
        header: "Date",
        cell: ({ row }) => new Date(row.original.created_at).toLocaleDateString(),
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => getStatusBadge(row.original.status ?? "pending"),
      },
      {
        id: "actions",
        header: () => <div className="text-right">Actions</div>,
        cell: ({ row }) => {
          const s = row.original;
          if (s.status !== "pending") {
            return (
              <div className="text-right">
                <span className="text-muted-foreground italic">Processed</span>
              </div>
            );
          }
          const busy = !!processingIds[s.id];
          return (
            <div className="flex justify-end gap-2">
              <Button
                size="sm"
                variant="outline"
                className="h-8 bg-green-50 hover:bg-green-100 text-green-700"
                onClick={() => updateSuggestionStatus(s, "approved")}
                disabled={busy}
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}
                Approve
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-8 bg-red-50 hover:bg-red-100 text-red-700"
                onClick={() => updateSuggestionStatus(s, "rejected")}
                disabled={busy}
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4 mr-1" />}
                Reject
              </Button>
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [processingIds],
  );

  return (
    <div className="space-y-3">
      <ListToolbar
        search={search}
        onSearchChange={(v) => url.set({ q: v || null, page: 0 })}
        searchPlaceholder="Search by category name or grade…"
        filters={
          <Select
            value={statusFilter}
            onValueChange={(v) => url.set({ status: v, page: 0 })}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((s) => (
                <SelectItem key={s} value={s}>
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
        onExportCsv={() =>
          exportRowsAsCsv(
            "category-suggestions.csv",
            paginated.rows as unknown as Record<string, unknown>[],
          )
        }
      />

      <DataTable
        columns={columns}
        data={paginated.rows}
        isLoading={paginated.isLoading}
        isError={paginated.isError}
        onRetry={paginated.refetch}
        emptyTitle="No suggestions found"
        emptyDescription={
          statusFilter === "all"
            ? "There are no category suggestions yet."
            : `No ${statusFilter} suggestions.`
        }
        getRowId={(row) => row.id}
      />

      <DataPagination
        page={paginated.page}
        pageSize={paginated.pageSize}
        total={paginated.total}
        pageCount={paginated.pageCount}
        onPageChange={paginated.setPage}
        onPageSizeChange={paginated.setPageSize}
        isFetching={paginated.isFetching}
      />
    </div>
  );
};

export default CategorySuggestions;
